import * as React from 'react';
import { AddressModalData, AddressModalTitleKey } from '@/types/address/address.types';
import { BlueLinkSecondaryUnderlined } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getAddressID } from '@/redux/modules/modal.selectors';
import {
    getCatalogIsInstantAutopay,
    getCatalogPaymentProvider,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getDefaultBidderCreditCardId } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { getItemSummaryCatalogOnly } from '@/redux/modules/item/summary/itemSummary.selectors';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { RegisteredMessage } from '@/types/RegistrationLink';
import { submitRegisterForCatalog } from '@/redux/modules/registerForCatalog';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styled from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

export type NewItemCardRegisterLinkProps = {
    catalogId: number;
    className?: string;
    itemId?: number;
    primaryButton?: boolean;
    registerToBidMessageId?: string;
};

type RegistrationType = {
    cardId: number;
    catalogId: number;
    itemId: number;
};

const NewItemCardRegisterLink = ({
    catalogId,
    className,
    itemId,
    primaryButton,
    registerToBidMessageId = 'register_for_auction',
}: NewItemCardRegisterLinkProps) => {
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const addressId = useAppSelector(getAddressID);
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const cardId = useAppSelector((state) => getDefaultBidderCreditCardId(state, paymentProvider));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryCatalogOnly(state, itemId));

    const dispatch = useAppDispatch();

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: itemId || catalogId,
            itemId,
            type: itemId
                ? CompleteAccountModalContext.RegisterForAuctionItem
                : CompleteAccountModalContext.RegisterForAuction,
        }),
        [itemId, catalogId]
    );

    const { approved, declined, pending, registered } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const addressData: AddressModalData = {
        addressID: addressId,
        titleKey: AddressModalTitleKey.RegisterForAuction,
    };
    const registerForCatalog = ({ cardId, catalogId, itemId }: RegistrationType) => {
        dispatch(submitRegisterForCatalog({ cardId, catalogId, itemId }));
    };
    const openRegisterModal = ({ catalogId, itemId }: RegistrationType) => {
        dispatch(openRegisterForAuctionModal({ catalogId, isInstantAutopay, itemId }));
    };

    if (isCatalogOnly) {
        if (registered || pending) {
            return (
                <BodySecondary color="secondary">
                    <FormattedMessage id="onlineAuctionRequested" />
                </BodySecondary>
            );
        }
        return (
            <ShouldLogInContainer<{
                cardId: number;
                catalogId: number;
                itemId: number;
            }>
                completeAddressData={addressData}
                requireCompleteProfile
                requireLogin
                requireVerifiedAddress
                runFunction={registerForCatalog}
                runFunctionArgument={{ cardId, catalogId, itemId }}
            >
                <StyledLinkRequestOnlineBidding className={className}>
                    <FormattedMessage id="requestOnlineAuction" />
                </StyledLinkRequestOnlineBidding>
            </ShouldLogInContainer>
        );
    }

    if (declined) {
        return (
            <DeniedSpan
                className={className}
                color={!isMobile ? 'secondary' : 'primary'}
            >
                <FormattedMessage
                    id={
                        isMobile
                            ? 'registration_status_label.registration_declined'
                            : 'registration_status_label.registration_denied'
                    }
                />
            </DeniedSpan>
        );
    }
    if (!registered) {
        const buttonProps = primaryButton ? { primary: true } : { tertiary: true };
        return (
            <ShouldLogInContainer<{ catalogId: number; itemId: number }>
                completeAddressData={addressData}
                modalContext={modalContext}
                requireCompleteProfile
                requireLogin
                requireVerifiedAddress
                runFunction={openRegisterModal}
                runFunctionArgument={{ catalogId, itemId }}
            >
                <StyledButton
                    className={className}
                    small
                    {...buttonProps}
                >
                    <FormattedMessage id={registerToBidMessageId} />
                </StyledButton>
            </ShouldLogInContainer>
        );
    }

    if (pending) {
        return (
            <BodySecondary color="secondary">
                <FormattedMessage id={RegisteredMessage.RegistrationPending} />
            </BodySecondary>
        );
    }

    if (approved || registered) {
        return null;
    }

    return (
        <ShouldLogInContainer<{ catalogId: number; itemId: number }>
            completeAddressData={addressData}
            modalContext={modalContext}
            requireCompleteProfile
            requireLogin
            requireVerifiedAddress
            runFunction={openRegisterModal}
            runFunctionArgument={{ catalogId, itemId }}
        >
            <InlineButton className={className}>
                <FormattedMessage id={registerToBidMessageId} />
            </InlineButton>
        </ShouldLogInContainer>
    );
};

export default React.memo<NewItemCardRegisterLinkProps>(NewItemCardRegisterLink);

const DeniedSpan = styled(BodySecondary)`
    display: inline-block;
    text-decoration: none !important;
    user-select: none !important;
    color: ${({ theme }) => theme.colors.red100};
    @media print {
        display: none;
    }
`;

const StyledButton = styled(Button)`
    text-transform: uppercase;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding-left: 8px;
        padding-right: 8px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        padding-left: 8px;
        padding-right: 8px;
    }
`;

const StyledLinkRequestOnlineBidding = styled(BlueLinkSecondaryUnderlined)`
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
